import { useState, useCallback, useRef, useEffect } from 'react'
import { Outlet, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { TopBarProvider } from '@/contexts/TopBarContext'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export function AppLayout() {
    const { user, loading } = useAuth()
    const { workspaces, loading: workspaceLoading } = useWorkspace()
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

    const location = useLocation()
    const mainRef = useRef<HTMLElement>(null)

    const handleMobileOpen = useCallback(() => setMobileSidebarOpen(true), [])
    const handleMobileClose = useCallback(() => setMobileSidebarOpen(false), [])

    // Scroll main content to top on route change
    useEffect(() => {
        mainRef.current?.scrollTo(0, 0)
    }, [location.pathname])

    if (loading || workspaceLoading) {
        return (
            <div className="min-h-screen grid place-items-center bg-background">
                <Skeleton className="h-12 w-12 rounded-full" />
            </div>
        )
    }

    if (!user) {
        return <Navigate to="/" replace />
    }

    // If user has no workspaces, redirect to onboarding setup
    if (workspaces.length === 0) {
        return <Navigate to="/app/onboarding/setup" replace />
    }

    return (
        <TopBarProvider>
            <div className="flex h-screen w-full overflow-hidden bg-background">
                {/* Sidebar */}
                <Sidebar
                    collapsed={sidebarCollapsed}
                    onCollapsedChange={setSidebarCollapsed}
                    mobileOpen={mobileSidebarOpen}
                    onMobileClose={handleMobileClose}
                />

                {/* Main Content Wrapper */}
                <div className="flex-1 flex flex-col h-full relative min-w-0 z-0">
                    {/* Top Bar */}
                    <TopBar onMobileMenuClick={handleMobileOpen} />

                    {/* Main Workspace (Scrollable) */}
                    <main
                        ref={mainRef}
                        className={cn(
                            'flex-1 overflow-y-auto overflow-x-hidden scroll-smooth custom-scrollbar'
                        )}
                    >
                        <Outlet />
                    </main>
                </div>
            </div>
        </TopBarProvider>
    )
}
